import React from "react";
import { Zap, Heart, Trophy, Flame, X, Target, Skull, Crown } from "lucide-react";

function fmt(n) { return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + "k" : n; }

export function HUD({ hud, onQuit, combo }) {
  const time = `${Math.floor(hud.timeLeft / 60)}:${String(hud.timeLeft % 60).padStart(2, "0")}`;
  const low = hud.timeLeft <= 30;
  return (
    <div className="pointer-events-none absolute inset-0 z-40 font-head select-none">
      {/* TOP BAR */}
      <div className="absolute top-0 left-0 right-0 flex items-start justify-between p-2 sm:p-3 gap-2">
        {/* left: level + xp + tier */}
        <div className="card px-3 py-2 min-w-[150px] sm:min-w-[210px]" data-testid="hud-level">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center text-black font-black text-base sm:text-lg" data-testid="hud-level-value">{hud.level}</div>
            <div className="flex-1">
              <div className="text-[10px] sm:text-xs text-amber-400 font-bold uppercase tracking-wide truncate">{hud.tierName}</div>
              <div className="mt-1 h-2 sm:h-2.5 w-full rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${hud.xpPct * 100}%` }} />
              </div>
            </div>
          </div>
        </div>

        {/* center: timer + score */}
        <div className="flex flex-col items-center gap-1">
          <div className={`card px-4 py-1.5 font-mono font-bold text-lg sm:text-2xl ${low ? "text-red-400 animate-pulse" : "text-white"}`} data-testid="hud-timer">{time}</div>
          <div className={`card px-4 py-1 flex items-center gap-2 ${hud.isLeader ? "border-amber-400/80" : ""}`} data-testid="hud-score">
            <Trophy size={16} className="text-amber-400" />
            <span className="text-white font-black text-base sm:text-xl">{hud.score.toLocaleString()}</span>
          </div>
          {hud.isLeader && (
            <div className="card px-3 py-1 flex items-center gap-1.5 border-amber-400/80 glow-pulse" data-testid="hud-leader-badge">
              <Crown size={14} className="text-amber-400" />
              <span className="text-amber-300 font-black text-[11px] uppercase tracking-wide">You're #1 — Bounty!</span>
            </div>
          )}
        </div>

        {/* right: rank + quit */}
        <div className="flex flex-col items-end gap-1">
          <div className="card px-3 py-2 flex items-center gap-2" data-testid="hud-rank">
            <span className="text-[10px] text-slate-400 uppercase font-bold">Rank</span>
            <span className={`font-black text-lg sm:text-2xl ${hud.rank === 1 ? "text-amber-400" : "text-white"}`}>#{hud.rank}</span>
            <span className="text-xs text-slate-500">/{hud.total}</span>
          </div>
          <button onClick={onQuit} className="pointer-events-auto card px-2 py-1.5 hover:border-red-500/60" data-testid="hud-quit-btn">
            <X size={16} className="text-slate-400" />
          </button>
        </div>
      </div>

      {/* LEADERBOARD */}
      <div className="absolute top-24 sm:top-28 right-2 sm:right-3 card p-2 w-32 sm:w-48 block" data-testid="hud-leaderboard">
        <div className="text-[10px] text-cyan-400 font-bold uppercase tracking-widest mb-1.5 px-1">Leaderboard</div>
        {hud.leaderboard.map((e, i) => (
          <div key={e.pid || `${e.name}-${i}`} className={`flex items-center gap-1.5 px-1.5 py-1 rounded text-xs ${e.isPlayer ? "bg-amber-500/20" : ""}`}>
            <span className="w-4 text-slate-500 font-bold">{e.rank || i + 1}</span>
            <span className="w-2 h-2 rounded-full" style={{ background: e.color }} />
            <span className={`flex-1 truncate font-semibold ${e.isPlayer ? "text-amber-300" : "text-slate-300"}`}>{e.name}</span>
            <span className="text-slate-400 font-mono">{fmt(e.score)}</span>
          </div>
        ))}
      </div>

      {/* COMBO */}
      {hud.combo >= 2 && (
        <div key={combo?.id} className="absolute left-1/2 -translate-x-1/2 top-28 sm:top-32 text-center slam-in" data-testid="hud-combo">
          <div className="font-black text-4xl sm:text-6xl text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-yellow-300 to-orange-500 drop-shadow-[0_0_16px_rgba(255,204,0,0.8)]">
            x{hud.combo}
          </div>
          <div className="text-cyan-300 font-bold text-sm sm:text-lg -mt-1">{hud.comboMult}x MULTIPLIER</div>
        </div>
      )}

      {/* BOTTOM: health + boost (bottom-center on mobile to clear the joystick, bottom-left on desktop) */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 sm:left-3 sm:translate-x-0 flex flex-col gap-1.5 w-40 sm:w-56" data-testid="hud-bars">
        <div className="card px-2 py-1.5">
          <div className="flex items-center gap-2">
            <Heart size={16} className="text-red-400 shrink-0" />
            <div className="flex-1 h-3 sm:h-4 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-red-500 to-rose-400 transition-all" style={{ width: `${(hud.health / hud.maxHealth) * 100}%` }} data-testid="hud-health" />
            </div>
          </div>
        </div>
        <div className="card px-2 py-1.5">
          <div className="flex items-center gap-2">
            <Zap size={16} className={`shrink-0 ${hud.boosting ? "text-cyan-300 animate-pulse" : "text-cyan-400"}`} />
            <div className="flex-1 h-3 sm:h-4 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-400 transition-all" style={{ width: `${(hud.boost / hud.boostMax) * 100}%` }} data-testid="hud-boost" />
            </div>
          </div>
        </div>
        <div className="flex gap-1.5 text-[10px] sm:text-xs">
          <div className="card px-2 py-1 flex items-center gap-1"><Flame size={12} className="text-orange-400" /><span className="text-slate-300 font-bold">{hud.objects}</span></div>
          <div className="card px-2 py-1 flex items-center gap-1"><Skull size={12} className="text-red-400" /><span className="text-slate-300 font-bold">{hud.kills}</span></div>
        </div>
      </div>

      {/* MINIMAP */}
      <div className="absolute bottom-3 right-3 card p-1 hidden sm:block" data-testid="hud-minimap">
        <div className="relative bg-slate-900/80 rounded" style={{ width: 120, height: 120 }}>
          {hud.minimap.ents.map((e, i) => (
            <div key={i} className="absolute rounded-full" style={{
              left: (e.x / hud.minimap.w) * 120 - (e.isPlayer ? 3 : 2),
              top: (e.y / hud.minimap.h) * 120 - (e.isPlayer ? 3 : 2),
              width: e.isPlayer ? 6 : 4, height: e.isPlayer ? 6 : 4,
              background: e.isPlayer ? "#FF9500" : e.color,
              boxShadow: e.isPlayer ? "0 0 6px #FF9500" : "none",
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}
